import { FlyoutListMenuWidget, MenuButtonWidget, MenuItemInfo } from '@tableau/widgets';
import * as React from 'react';
import { style } from 'typestyle';

import { Icons } from '~commonInfra/components/Icons';
import { Messages } from '~src/codegen/Localize';

import { IAdminProfile } from '../models/IAdminProfile';

export interface AdminProfileActionMenuProps {
  profile: IAdminProfile;
  onEdit?: (profile: IAdminProfile) => void;
  onDelete?: (profile: IAdminProfile) => void;
}

const ACTION_EDIT = 'edit';
const ACTION_DELETE = 'delete';

const menuButtonDropdownStyle = style(
  { $nest: { '&&::after': { display: 'none' }, '&:disabled': { borderColor: 'transparent !important' } } }
);

const menuButtonStyle: React.CSSProperties = {
  borderColor: 'transparent', padding: 0, minWidth: 24, height: 24,
};

export const AdminProfileActionMenu: React.FC<AdminProfileActionMenuProps> = ({
  profile,
  onEdit,
  onDelete,
}) => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  const handleMenuSelect = React.useCallback((value: string) => {
    setIsMenuOpen(false);
    if (value === ACTION_EDIT && onEdit) {
      onEdit(profile);
    } else if (value === ACTION_DELETE && onDelete) {
      onDelete(profile);
    }
  }, [profile, onEdit, onDelete]);

  const handleMenuClick = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMenuOpen(prev => !prev);
  }, []);

  const menuItems: MenuItemInfo[] = React.useMemo(() => [
    { displayValue: Messages.adminProfileHeaderEditButton(), value: ACTION_EDIT },
    { displayValue: Messages.adminProfileHeaderDeleteButton(), value: ACTION_DELETE },
  ], []);

  const actionsMenuId = `admin-profile-actions-${profile.id}`;

  return (
    <>
      <MenuButtonWidget
        aria-controls={actionsMenuId}
        isOpen={isMenuOpen}
        onClick={handleMenuClick}
        ref={buttonRef}
        testId={`admin-profile-actions-menu-${profile.id}`}
        density='high'
        className={menuButtonDropdownStyle}
        style={menuButtonStyle}
      >
        <Icons.actionIcon hovered={isMenuOpen} style={{ verticalAlign: 'middle' }} />
      </MenuButtonWidget>
      {isMenuOpen && (
        <FlyoutListMenuWidget
          role='menu'
          anchorEl={buttonRef.current}
          allowAutoPosition
          id={actionsMenuId}
          menuItemInfos={menuItems}
          onSelect={handleMenuSelect}
          requestCloseAll={() => setIsMenuOpen(false)}
          testId={`admin-profile-actions-menu-list-${profile.id}`}
          toggleEl={buttonRef.current}
        />
      )}
    </>
  );
};

AdminProfileActionMenu.displayName = 'AdminProfileActionMenu';
